import retry from 'async-retry';

import { removeDraftsFromContentful } from 'utils/Contentful';
import DataDogLogs from 'utils/DataDogLogs';
import Settings from 'utils/Settings';

import {
  ENDPOINT_DOMAIN,
  ENDPOINT_HTTP_METHOD,
  IGNORABLE_ERROR_CODES,
  RETRIABLE_ERROR_CODES,
  RETRY_CALL_OPTIONS,
} from './constants';

class Contentful {
  static call = async ({ query, variables }) => {
    // Helper variables
    const method = ENDPOINT_HTTP_METHOD;
    const endpoint = ENDPOINT_DOMAIN;
    const environment = Settings?.contentfulEnvironment;
    const spaceId = Settings?.contentfulSpaceId;
    const accessToken = Settings?.contentfulAccessToken;
    const url = `${endpoint}/content/v1/spaces/${spaceId}/environments/${environment}`;
    const body = JSON.stringify({ query, variables });
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    };
    const loggerFields = {
      http: {
        url,
        method,
        url_details: {
          host: endpoint,
        },
      },
    };
    let additionalLoggerFields = {
      query,
      variables,
    };
    let endTime, duration;
    const startTime = Date.now();

    // Validations
    if (!environment) {
      throw new Error('Missing contentful environment - Unable to hit Contentful API.');
    }

    if (!spaceId) {
      throw new Error('Missing contentful spaceId - Unable to hit Contentful API.');
    }

    if (!accessToken) {
      throw new Error('Missing contentful access token - Unable to hit Contentful API.');
    }

    if (!query) {
      throw new Error(`Missing query - Unable to hit Contentful API.`);
    }

    // Calling api with retry feature
    try {
      return await retry(async (bail, attempt) => {
        let apiResponse;

        try {
          apiResponse = await fetch(url, {
            method,
            headers,
            body,
          });
        } catch (e) {
          // Don't retry fetch errors (e.g. Network errors)
          bail(e);
        }

        // process response header
        let responseHeaders = {};
        for (const [key, value] of apiResponse.headers.entries()) {
          responseHeaders[key] = value;
        }

        endTime = Date.now();
        duration = endTime - startTime;

        // collecting metrics logs
        DataDogLogs.info(`Contentful:call`, {
          duration,
          attempt,
          ...loggerFields,
          ...additionalLoggerFields,
          http: {
            ...loggerFields.http,
            ...(apiResponse.status ? { status_code: apiResponse.status } : {}),
          },
          contentful: {
            ...responseHeaders,
          },
        });

        const { data, errors } = await apiResponse.json();

        // Handle retry errors
        if (errors) {
          for (const error of errors) {
            const errorCode = error.extensions?.contentful?.code;

            const isIgnorable = !!IGNORABLE_ERROR_CODES[errorCode];
            const isRetriable = !!RETRIABLE_ERROR_CODES[errorCode];

            if (isIgnorable) {
              continue;
            }

            if (isRetriable) {
              throw new Error(error.message);
            } else {
              bail(new Error(error.message));
            }
          }
        }

        // Data structure response is hanlded by clients of this method
        return removeDraftsFromContentful(data);
      }, RETRY_CALL_OPTIONS);
    } catch (error) {
      endTime = Date.now();
      duration = endTime - startTime;

      DataDogLogs.error(
        `Contentful:call - Error: ${error.name}`,
        {
          duration,
          ...loggerFields,
          ...additionalLoggerFields,
        },
        error
      );

      throw new Error(error);
    }
  };
}

export default Contentful;
